import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Image as ImageIcon, 
  Download, 
  Eye, 
  Trash2, 
  Upload, 
  Search, 
  Filter, 
  Calendar,
  File,
  X,
  Loader2,
  AlertCircle,
  Grid3X3,
  List,
  Check,
  CheckSquare,
  Square,
  User
} from 'lucide-react';
import { getUserFiles, deleteFile } from '../services/fileUploadApi';
import { useAppearance } from '../contexts/AppearanceContext';
import { useSession } from '../contexts/SessionContext';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from './ConfirmationModal';

const FilesManager = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, images, documents
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, name, size
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  
  const { isSuppleMode, appearanceMode } = useAppearance();
  const { sessionData } = useSession();
  const { user: authUser } = useAuth();
  const currentUser = sessionData?.user || authUser;

  useEffect(() => {
    console.log('Full session data:', sessionData);
    console.log('Auth user:', authUser);
    console.log('Current user:', currentUser);
    console.log('Session data changed:', sessionData?.loading, sessionData?.user?.email);
    // Load files if sessionData is undefined (fallback) or if session is loaded
    if (!sessionData || (sessionData && !sessionData.loading)) {
      console.log('Loading files - sessionData undefined or loaded');
      loadFiles();
    }
  }, [sessionData]);

  // Reload files when user session changes (in case Super Admin settings change)
  useEffect(() => {
    if (currentUser?.isSuperAdmin) {
      loadFiles();
    }
  }, [currentUser?.isSuperAdmin]);

  // Fallback: Load files after 2 seconds if session seems stuck
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('Fallback: Loading files after timeout');
      if (files.length === 0 && !loading) {
        loadFiles();
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const loadFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUserFiles();
      console.log('Files API response:', response);
      console.log('Number of files loaded:', response.files?.length || 0);
      console.log('Current user:', currentUser?.email, 'isSuperAdmin:', currentUser?.isSuperAdmin);
      console.log('Files with user data:', response.files?.filter(f => f.user).length || 0);
      setFiles(response.files || []);
    } catch (error) {
      console.error('Failed to load files:', error);
      setError('Failed to load files. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async () => {
    if (!fileToDelete) return;
    
    try {
      await deleteFile(fileToDelete.id);
      setFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
      setFileToDelete(null);
      setDeleteModalOpen(false);
      if (selectedFile && selectedFile.id === fileToDelete.id) {
        setSelectedFile(null);
      }
    } catch (error) {
      console.error('Failed to delete file:', error);
      setError('Failed to delete file. Please try again.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    
    return date.toLocaleDateString();
  };

  const getFileIcon = (file) => {
    if (file.isImage) {
      return <ImageIcon className="w-5 h-5 text-blue-600" />;
    }
    return <FileText className="w-5 h-5 text-gray-600" />;
  };

  // Helper function to render user indicator for files from other users
  const renderUserIndicator = (file) => {
    if (!file.user || !currentUser || file.user.id === currentUser.id) {
      return null;
    }

    return (
      <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
        {file.user.picture ? (
          <img 
            src={file.user.picture} 
            alt={file.user.name}
            className="w-4 h-4 rounded-full"
          />
        ) : (
          <User className="w-3 h-3" />
        )}
        <span>{file.user.name || file.user.email}</span>
      </div>
    );
  };

  const filteredAndSortedFiles = files
    .filter(file => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!file.filename.toLowerCase().includes(query)) {
          return false;
        }
      }
      
      // Type filter
      if (filterType === 'images' && !file.isImage) return false;
      if (filterType === 'documents' && !file.isDocument) return false;
      
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'name':
          return a.filename.localeCompare(b.filename);
        case 'size':
          return b.size - a.size;
        case 'newest':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

  const handleViewFile = (file) => {
    if (file.isImage && file.url) {
      setPreviewFile(file);
      setPreviewModalOpen(true);
    } else if (file.url) {
      window.open(file.url, '_blank');
    }
  };

  const handleDownloadFile = (file) => {
    if (file.url) {
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleFileSelect = (fileId, isSelected) => {
    const newSelectedFiles = new Set(selectedFiles);
    if (isSelected) {
      newSelectedFiles.add(fileId);
    } else {
      newSelectedFiles.delete(fileId);
    }
    setSelectedFiles(newSelectedFiles);
  };

  const handleSelectAll = () => {
    if (selectedFiles.size === filteredAndSortedFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(filteredAndSortedFiles.map(f => f.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.size === 0) return;
    
    try {
      const deletePromises = Array.from(selectedFiles).map(fileId => deleteFile(fileId));
      await Promise.all(deletePromises);
      
      setFiles(prev => prev.filter(f => !selectedFiles.has(f.id)));
      setSelectedFiles(new Set());
      setBulkDeleteModalOpen(false);
    } catch (error) {
      console.error('Failed to delete files:', error);
      setError('Failed to delete some files. Please try again.');
    }
  };

  const totalSize = files.reduce((acc, file) => acc + file.size, 0);
  const imageCount = files.filter(f => f.isImage).length;
  const documentCount = files.filter(f => f.isDocument).length;

  if (loading) {
    return (
      <div className={`flex-1 flex items-center justify-center ${
        isSuppleMode ? 'supple-bg-primary' : 'bg-white'
      }`}>
        <div className="text-center">
          <Loader2 className={`w-8 h-8 animate-spin mx-auto mb-4 ${
            isSuppleMode ? 'supple-text-muted' : 'text-gray-400'
          }`} />
          <p className={isSuppleMode ? 'supple-text-muted' : 'text-gray-500'}>
            Loading files... (sessionData: {sessionData ? 'exists' : 'undefined'})
          </p>
          <button
            onClick={loadFiles}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Force Load Files
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex-1 flex items-center justify-center ${
        isSuppleMode ? 'supple-bg-primary' : 'bg-white'
      }`}>
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadFiles}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isSuppleMode 
                ? 'supple-btn-gradient-blue supple-shadow-md hover:supple-btn-gradient-blue-hover' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`h-full flex flex-col ${
        isSuppleMode ? 'supple-bg-primary' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`p-6 flex-shrink-0 ${
          isSuppleMode ? 'supple-border-subtle border-b' : 'border-b border-gray-200'
        }`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className={`text-2xl font-bold ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                }`}>
                  Files Manager
                </h1>
                {currentUser?.isSuperAdmin && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                    Super Admin View
                  </span>
                )}
              </div>
              <p className={`text-sm mt-1 ${
                isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
              }`}>
                {currentUser?.isSuperAdmin 
                  ? 'Viewing all files across the platform (if enabled in Privacy & Security)'
                  : 'Manage your uploaded files and attachments'
                }
              </p>
            </div>
            
            {/* Stats and Actions */}
            <div className="flex gap-4 items-center">
              <div className={`px-3 py-2 rounded-lg ${
                isSuppleMode ? 'supple-card-glass' : 'bg-gray-50 border'
              }`}>
                <div className={`text-xs ${isSuppleMode ? 'supple-text-muted' : 'text-gray-500'}`}>
                  Total Files
                </div>
                <div className={`font-semibold ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                }`}>
                  {files.length}
                </div>
              </div>
              <div className={`px-3 py-2 rounded-lg ${
                isSuppleMode ? 'supple-card-glass' : 'bg-gray-50 border'
              }`}>
                <div className={`text-xs ${isSuppleMode ? 'supple-text-muted' : 'text-gray-500'}`}>
                  Total Size
                </div>
                <div className={`font-semibold ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                }`}>
                  {formatFileSize(totalSize)}
                </div>
              </div>
              
              {/* Reload button for debugging */}
              <button
                onClick={loadFiles}
                className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                  isSuppleMode 
                    ? 'supple-btn-gradient-blue hover:supple-btn-gradient-blue-hover' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Reload'}
              </button>
            </div>
          </div>
          
          {/* Search and Filters */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                isSuppleMode ? 'supple-text-muted' : 'text-gray-400'
              }`} />
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                  isSuppleMode 
                    ? 'supple-input-bg supple-border-subtle supple-text-primary' 
                    : 'border-gray-300 bg-white text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
            </div>
            
            <div className="flex gap-2">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className={`px-3 py-2 rounded-lg border ${
                  isSuppleMode 
                    ? 'supple-input-bg supple-border-subtle supple-text-primary' 
                    : 'border-gray-300 bg-white text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <option value="all">All Files</option>
                <option value="images">Images ({imageCount})</option>
                <option value="documents">Documents ({documentCount})</option>
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={`px-3 py-2 rounded-lg border ${
                  isSuppleMode 
                    ? 'supple-input-bg supple-border-subtle supple-text-primary' 
                    : 'border-gray-300 bg-white text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name">Name (A-Z)</option>
                <option value="size">Size (Largest)</option>
              </select>

              {/* View toggle */}
              <div className="flex border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${
                    viewMode === 'grid'
                      ? isSuppleMode 
                        ? 'supple-btn-gradient-blue supple-text-primary'
                        : 'bg-blue-600 text-white'
                      : isSuppleMode
                        ? 'supple-text-muted hover:supple-text-primary'
                        : 'text-gray-600 hover:text-gray-900'
                  } transition-colors`}
                  title="Grid view"
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${
                    viewMode === 'list'
                      ? isSuppleMode 
                        ? 'supple-btn-gradient-blue supple-text-primary'
                        : 'bg-blue-600 text-white'
                      : isSuppleMode
                        ? 'supple-text-muted hover:supple-text-primary'
                        : 'text-gray-600 hover:text-gray-900'
                  } transition-colors`}
                  title="List view"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Bulk actions */}
          {selectedFiles.size > 0 && (
            <div className={`mt-3 p-3 rounded-lg border ${
              isSuppleMode ? 'supple-card-glass supple-border-subtle' : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm ${
                  isSuppleMode ? 'supple-text-primary' : 'text-blue-900'
                }`}>
                  {selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''} selected
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedFiles(new Set())}
                    className={`px-3 py-1 text-xs rounded ${
                      isSuppleMode 
                        ? 'supple-text-muted hover:supple-text-primary' 
                        : 'text-gray-600 hover:text-gray-900'
                    } transition-colors`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => setBulkDeleteModalOpen(true)}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Delete Selected
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Files Display */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header with select all */}
          {filteredAndSortedFiles.length > 0 && (
            <div className={`p-4 border-b ${
              isSuppleMode ? 'supple-border-subtle' : 'border-gray-200'
            }`}>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSelectAll}
                  className={`flex items-center gap-2 text-sm ${
                    isSuppleMode ? 'supple-text-primary hover:supple-text-primary' : 'text-gray-700 hover:text-gray-900'
                  } transition-colors`}
                >
                  {selectedFiles.size === filteredAndSortedFiles.length ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : selectedFiles.size > 0 ? (
                    <CheckSquare className="w-4 h-4 opacity-50" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  Select All
                </button>
                <span className={`text-sm ${
                  isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                }`}>
                  {filteredAndSortedFiles.length} files
                </span>
              </div>
            </div>
          )}

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-6">
              {filteredAndSortedFiles.length === 0 ? (
                <div className="text-center py-12">
                  <File className={`w-12 h-12 mx-auto mb-4 ${
                    isSuppleMode ? 'supple-text-muted' : 'text-gray-400'
                  }`} />
                  <h3 className={`text-lg font-medium mb-2 ${
                    isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                  }`}>
                    {searchQuery || filterType !== 'all' ? 'No matching files' : 'No files uploaded'}
                  </h3>
                  <p className={`text-sm ${
                    isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                  }`}>
                    {searchQuery || filterType !== 'all' 
                      ? 'Try adjusting your search or filter criteria'
                      : 'Upload files in chat to see them here'
                    }
                  </p>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredAndSortedFiles.map((file) => (
                    <div
                      key={file.id}
                      className={`group relative p-4 rounded-lg border transition-all duration-200 hover:scale-105 ${
                        selectedFiles.has(file.id)
                          ? isSuppleMode 
                            ? 'supple-card-glass supple-border-accent ring-2 ring-blue-500'
                            : 'bg-blue-50 border-blue-300 ring-2 ring-blue-500'
                          : isSuppleMode 
                            ? 'supple-card-glass supple-border-subtle hover:supple-card-glass-hover supple-shadow-md' 
                            : 'bg-white border-gray-200 hover:shadow-lg hover:border-gray-300'
                      }`}
                    >
                      {/* Selection checkbox */}
                      <div className="absolute top-2 right-2 z-10">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileSelect(file.id, !selectedFiles.has(file.id));
                          }}
                          className={`p-1 rounded transition-opacity ${
                            selectedFiles.has(file.id) || selectedFiles.size > 0 
                              ? 'opacity-100' 
                              : 'opacity-0 group-hover:opacity-100'
                          }`}
                        >
                          {selectedFiles.has(file.id) ? (
                            <CheckSquare className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Square className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>

                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0 pr-6">
                          {getFileIcon(file)}
                          <h3 className={`font-medium mt-2 truncate ${
                            isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                          }`} title={file.filename}>
                            {file.filename}
                          </h3>
                          <p className={`text-xs mt-1 ${
                            isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                          }`}>
                            {formatFileSize(file.size)} • {formatDate(file.createdAt)}
                          </p>
                          {/* User indicator for files from other users */}
                          {renderUserIndicator(file) && (
                            <div className="mt-2">
                              {renderUserIndicator(file)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Preview for images */}
                      {file.isImage && file.url && (
                        <div className="mb-3">
                          <img
                            src={file.url}
                            alt={file.filename}
                            className="w-full h-32 object-cover rounded-lg cursor-pointer"
                            onClick={() => handleViewFile(file)}
                          />
                        </div>
                      )}

                      {/* Document preview text */}
                      {file.extractedText && (
                        <div className={`mb-3 p-2 rounded text-xs ${
                          isSuppleMode ? 'supple-bg-secondary supple-text-muted' : 'bg-gray-50 text-gray-600'
                        }`}>
                          <p className="line-clamp-3">
                            {file.extractedText.substring(0, 100)}...
                          </p>
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleViewFile(file)}
                          className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-lg transition-colors ${
                            isSuppleMode 
                              ? 'supple-text-primary hover:supple-card-glass-hover' 
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          title="View file"
                        >
                          <Eye className="w-3 h-3" />
                          View
                        </button>
                        <button
                          onClick={() => handleDownloadFile(file)}
                          className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-lg transition-colors ${
                            isSuppleMode 
                              ? 'supple-text-primary hover:supple-card-glass-hover' 
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          title="Download file"
                        >
                          <Download className="w-3 h-3" />
                          Download
                        </button>
                        <button
                          onClick={() => {
                            setFileToDelete(file);
                            setDeleteModalOpen(true);
                          }}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete file"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* List View */
                <div className="space-y-2">
                  {filteredAndSortedFiles.map((file) => (
                    <div
                      key={file.id}
                      className={`group p-4 rounded-lg border transition-all duration-200 ${
                        selectedFiles.has(file.id)
                          ? isSuppleMode 
                            ? 'supple-card-glass supple-border-accent ring-2 ring-blue-500'
                            : 'bg-blue-50 border-blue-300 ring-2 ring-blue-500'
                          : isSuppleMode 
                            ? 'supple-card-glass supple-border-subtle hover:supple-card-glass-hover' 
                            : 'bg-white border-gray-200 hover:shadow-md hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Selection checkbox */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileSelect(file.id, !selectedFiles.has(file.id));
                          }}
                          className="flex-shrink-0"
                        >
                          {selectedFiles.has(file.id) ? (
                            <CheckSquare className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Square className="w-4 h-4 text-gray-400" />
                          )}
                        </button>

                        {/* File icon and preview */}
                        <div className="flex-shrink-0">
                          {file.isImage && file.url ? (
                            <img
                              src={file.url}
                              alt={file.filename}
                              className="w-12 h-12 object-cover rounded cursor-pointer"
                              onClick={() => handleViewFile(file)}
                            />
                          ) : (
                            getFileIcon(file)
                          )}
                        </div>

                        {/* File info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`font-medium truncate ${
                              isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                            }`} title={file.filename}>
                              {file.filename}
                            </h3>
                            {renderUserIndicator(file)}
                          </div>
                          <p className={`text-sm ${
                            isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                          }`}>
                            {formatFileSize(file.size)} • {formatDate(file.createdAt)}
                            {file.extractedText && ' • Text extracted'}
                          </p>
                        </div>

                        {/* Action buttons */}
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <button
                            onClick={() => handleViewFile(file)}
                            className={`p-2 rounded-lg transition-colors ${
                              isSuppleMode 
                                ? 'supple-text-primary hover:supple-card-glass-hover' 
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            title="View file"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadFile(file)}
                            className={`p-2 rounded-lg transition-colors ${
                              isSuppleMode 
                                ? 'supple-text-primary hover:supple-card-glass-hover' 
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            title="Download file"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setFileToDelete(file);
                              setDeleteModalOpen(true);
                            }}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete file"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setFileToDelete(null);
        }}
        onConfirm={handleDeleteFile}
        title="Delete File"
        message={`Are you sure you want to delete "${fileToDelete?.filename}"? This will free up database storage space but cannot be undone.`}
        confirmText="Delete File"
        cancelText="Cancel"
        type="danger"
      />

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={bulkDeleteModalOpen}
        onClose={() => setBulkDeleteModalOpen(false)}
        onConfirm={handleBulkDelete}
        title="Delete Multiple Files"
        message={`Are you sure you want to delete ${selectedFiles.size} selected file${selectedFiles.size !== 1 ? 's' : ''}? This will free up database storage space but cannot be undone.`}
        confirmText={`Delete ${selectedFiles.size} File${selectedFiles.size !== 1 ? 's' : ''}`}
        cancelText="Cancel"
        type="danger"
      />

      {/* Preview Modal for Images */}
      {previewModalOpen && previewFile && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={() => setPreviewModalOpen(false)}
        >
          <div className="relative max-w-full max-h-full">
            <img 
              src={previewFile.url} 
              alt={previewFile.filename} 
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setPreviewModalOpen(false)}
              className="absolute top-4 right-4 p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100 transition-colors"
              title="Close"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadFile(previewFile);
              }}
              className="absolute bottom-4 right-4 p-3 bg-white rounded-lg shadow-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
              title="Download image"
            >
              <Download className="w-5 h-5 text-gray-700" />
              <span className="text-gray-700 font-medium">Download</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FilesManager;